import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Animated,
  BackHandler,
  Easing,
  KeyboardAvoidingView,
  Modal,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import styled, { css, useTheme } from 'styled-components';
import useBehaviorSubject, {
  useBehaviorSubjectUpdater,
} from '../../functions/hooks/useBehaviorSubject';

export const AppModalContext = createContext<
  BehaviorSubject<AppModalProps | null>
>(null as any);

export type AppModalProps = {
  children: ReactNode;
  visible: boolean;
  portal?: boolean;
  _modalKey: number;
  onDismiss?: () => void;
  backdropDismissible?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  accessoryView?: ReactNode;
  blurBackground?: boolean;
  backgroundColor?: string;
};

const AppModal = (props: Omit<AppModalProps, '_modalKey'>) => {
  const _modalKey = useRef(Math.random()).current;
  const appModalContent = useContext(AppModalContext);
  const setModalContent = useBehaviorSubjectUpdater(appModalContent);
  useEffect(() => {
    if (props.visible) {
      const sub = appModalContent
        .pipe(
          filter(a => a == null || a._modalKey === _modalKey),
          first(),
        )
        .subscribe({
          next: () => {
            appModalContent.next({ ...props, _modalKey });
          },
        });
      return () => sub.unsubscribe();
    } else {
      const sub = appModalContent
        .pipe(
          filter(a => a?._modalKey === _modalKey),
          first(),
        )
        .subscribe({
          next: () => {
            appModalContent.next(null);
          },
        });
      return () => sub.unsubscribe();
    }
  }, [_modalKey, props, appModalContent]);
  useEffect(() => {
    return () => setModalContent(p => (p?._modalKey === _modalKey ? null : p));
  }, [_modalKey, setModalContent]);
  return null;
};

export default AppModal;

const useAnimation = () => {
  const animation = useRef(new Animated.Value(0)).current;
  const swapAnimation = useRef(new Animated.Value(0)).current;
  const fade = useCallback(
    (direction: 'in' | 'out', callback?: () => void) => {
      Animated.timing(animation, {
        toValue: direction === 'in' ? 1 : 0,
        duration: 150,
        easing:
          direction === 'in'
            ? Easing.in(Easing.linear)
            : Easing.out(Easing.linear),
        useNativeDriver: true,
      }).start(({ finished }) => finished && callback?.());
    },
    [animation],
  );
  const swap = useCallback(
    (direction: 'out' | 'in', callback?: () => void) => {
      Animated.timing(swapAnimation, {
        duration: 150,
        useNativeDriver: true,
        easing:
          direction === 'in'
            ? Easing.in(Easing.linear)
            : Easing.out(Easing.linear),
        toValue: direction === 'out' ? 1 : 0,
      }).start(({ finished }) => finished && callback?.());
    },
    [swapAnimation],
  );
  const backgroundOpacity = animation;
  const cardOffset = Animated.add(animation, swapAnimation).interpolate({
    inputRange: [0, 1, 2],
    outputRange: [400, 0, 100],
  });
  const cardOpacity = swapAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0],
  });
  return {
    fade,
    swap,
    backgroundOpacity,
    cardOpacity,
    cardOffset,
  };
};

const Container = styled(Animated.View)`
  margin: 30px 5px;
  background-color: white;
  border-radius: 15px;
  align-self: center;
  max-width: 375px;
  width: 95%;
` as any;

const ScrollView = Animated.ScrollView as any;

const AppModalRoot = memo(() => {
  const appModalContent = useContext(AppModalContext);
  const [modalContent] = useBehaviorSubject(appModalContent);
  const [current, setCurrent] = useState<typeof modalContent>(null);
  const animation = useAnimation();
  useEffect(() => {
    if (modalContent != null && current == null) {
      animation.fade('in', () => {
        setCurrent(modalContent);
      });
      return;
    }
    if (modalContent == null && current != null) {
      animation.fade('out', () => setCurrent(null));
      return;
    }
    if (modalContent != null && modalContent._modalKey !== current?._modalKey) {
      animation.swap('out', () => {
        setCurrent(modalContent);
        animation.swap('in');
      });
      return;
    }
    setCurrent(modalContent);
  }, [modalContent, current, animation]);
  const rendering = current ?? modalContent!;
  useEffect(() => {
    if (rendering == null) {
      return;
    }
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      rendering?.onDismiss?.();
      return true;
    });
    return () => sub.remove();
  }, [rendering]);

  if (rendering == null) {
    return null;
  }

  if (rendering.portal) {
    return (
      <Modal transparent visible={rendering.visible}>
        <ModalContent {...rendering} animation={animation} />
      </Modal>
    );
  }

  return <ModalContent {...rendering} animation={animation} />;
});

const ModalContent = memo(
  ({
    animation,
    ...rendering
  }: AppModalProps & {
    animation: ReturnType<typeof useAnimation>;
  }) => {
    const backgroundColor = rendering.backgroundColor || 'rgba(0, 0, 0, 0.54)';

    return (
      <KeyboardAvoidingView
        behavior={'padding'}
        style={StyleSheet.absoluteFill}
      >
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
          style={{
            opacity: animation.backgroundOpacity,
            backgroundColor,
          }}
          css={
            rendering.blurBackground && Platform.OS === 'web'
              ? css`
                  backdrop-filter: blur(10px);
                  -webkit-backdrop-filter: blur(10px);
                `
              : undefined
          }
        >
          <TouchableWithoutFeedback
            onPress={
              rendering?.backdropDismissible === false
                ? undefined
                : () => rendering.onDismiss?.()
            }
          >
            <View style={StyleSheet.absoluteFill} />
          </TouchableWithoutFeedback>
          <Container
            style={[
              {
                opacity: animation.cardOpacity,
                transform: [
                  {
                    translateY: animation.cardOffset,
                  },
                ],
              },
              rendering.containerStyle,
            ]}
          >
            <View
              style={rendering.contentContainerStyle}
              css={`
                width: 100%;
              `}
            >
              {rendering.children}
            </View>
            {rendering.accessoryView}
          </Container>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  },
);

export const AppModalProvider = (props: { children: ReactNode }) => {
  const appModalContent = useMemo(
    () => new BehaviorSubject<AppModalProps | null>(null),
    [],
  );
  return (
    <AppModalContext.Provider value={appModalContent}>
      {props.children}
      <AppModalRoot />
    </AppModalContext.Provider>
  );
};

export const AppModalLoading = () => {
  const theme = useTheme();
  return (
    <View
      css={`
        height: 100px;
        justify-content: center;
        align-items: center;
      `}
    >
      <ActivityIndicator color={theme.colors.primaryDarkColor} />
    </View>
  );
};
